import { Typography, TooltipProps, Stack, Divider, ThemeProvider } from '@mui/material'
import { AppV1AppEntitlementsowner } from 'c1gen/roles'
import { C1Tooltip, maxTooltipWidth } from 'c1ui/components/C1Tooltip/C1Tooltip'
import { C1DarkTheme } from 'c1ui/theme/C1Theme'
import { C1LinkButton } from 'components/core/button/C1LinkButton'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { MetadataKeyValuePair } from 'components/core/page-header/MetadataKeyValuePair'
import { VariableText } from 'components/core/variable-text'
import { useHasRole } from 'components/no-ui/capability'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { ResourceDisplayName } from 'components/pages/entitlement/resource'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { routes } from 'lib/routes'
import { ExternalLink } from 'lucide-react'
import React, { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type TooltipEntitlementProps = {
    entitlement: AppEntitlementValue | undefined
    children: React.ReactElement
} & Pick<TooltipProps, 'disableHoverListener'>

export type EntitlementTooltipContentProps = {
    entitlement: AppEntitlementValue
}

export const EntitlementTooltipContent: FC<EntitlementTooltipContentProps> = ({ entitlement }) => {
    const intl = useIntl()

    let sourceText = <FormattedMessage id="entitlement_source_connector" defaultMessage="Connector managed" />
    if (entitlement.systemBuiltin) {
        sourceText = <FormattedMessage id="entitlement_source_builtin" defaultMessage="System managed" />
    } else if (entitlement.isManuallyManaged) {
        sourceText = <FormattedMessage id="entitlement_source_manual" defaultMessage="Manually managed" />
    }

    return (
        <ThemeProvider theme={C1DarkTheme}>
            <Stack gap={1} py={1} sx={{ maxWidth: maxTooltipWidth }}>
                <Stack direction="row" gap={2} mb={1}>
                    <Typography variant="h4">{entitlement.displayName}</Typography>
                </Stack>

                <Divider sx={{ my: 1, borderColor: 'divider' }} />

                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_description',
                        defaultMessage: 'Description',
                    })}
                    metadataValue={entitlement.description || <C1EmptyIndicator />}
                    isCopyable
                    copyValue={entitlement.description}
                    fullWidth
                />

                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_id',
                        defaultMessage: 'ID',
                    })}
                    metadataValue={<VariableText variant="monospace" variableText={entitlement.id} />}
                    isCopyable
                    copyValue={entitlement.id}
                    fullWidth
                />

                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_slug',
                        defaultMessage: 'Slug',
                    })}
                    metadataValue={<VariableText variableText={entitlement.slug} />}
                    isCopyable
                    copyValue={entitlement.slug}
                    fullWidth
                />

                {entitlement.app && <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_app',
                        defaultMessage: 'App',
                    })}
                    metadataValue={<AppDisplayName app={entitlement.app} />}
                    isCopyable
                    copyValue={entitlement.app.id}
                    fullWidth
                    noTruncation
                />}

                {entitlement.appResource && <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_resource',
                        defaultMessage: 'Resource',
                    })}
                    metadataValue={<ResourceDisplayName resource={entitlement.appResource} />}
                    isCopyable
                    copyValue={entitlement.appResource.id}
                    fullWidth
                    noTruncation
                />}

                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_resource_type',
                        defaultMessage: 'Resource type',
                    })}
                    metadataValue={<VariableText variableText={entitlement.appResourceType?.displayName} />}
                    fullWidth
                />

                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_entitlement_source',
                        defaultMessage: 'Source',
                    })}
                    metadataValue={sourceText}
                    fullWidth
                    noTruncation
                />

                <Divider sx={{ mb: 1, mt: 2, borderColor: 'divider' }} />

                <C1LinkButton
                    href={routes.applications.entitlement(entitlement.appId, entitlement.id)}
                    target="_blank"
                    color="info"
                    variant="text"
                    endIcon={<C1Icon icon={ExternalLink} />}
                >
                    <FormattedMessage id="component_app_tooltip_view_entitlement_details" defaultMessage="View entitlement detail page" />
                </C1LinkButton>
            </Stack>
        </ThemeProvider>
    )
}
export const TooltipEntitlement: FC<TooltipEntitlementProps> = ({ entitlement, children, ...extraProps }) => {
    const isOwner = useHasRole(AppV1AppEntitlementsowner)
    return !entitlement || !isOwner ? children : (
        <C1Tooltip
            placement="right-end"
            PopperProps={{ sx: { '& .MuiTooltip-tooltip': { maxWidth: 'none' } } }}
            title={<EntitlementTooltipContent entitlement={entitlement} />}
            {...extraProps}
        >
            {children}
        </C1Tooltip>
    )
}
